import _ from 'lodash';
import moment from 'moment';
import {
  productCodes,
  formArrayTypeDivider,
  emsMainCodeList,
  chronicCareGeneralFormSettings,
  chronicCareCode,
  DATE_FORMAT,
  careProducts,
  promoProducts,
  plusProducts,
  completeProducts
} from '../constants/formConstants';

const todayDate = moment().format(DATE_FORMAT);

export function transformRecursively(data) {
  if (!_.isObject(data)) {
    return data;
  }

  if (_.isArray(data)) {
    return data.map(transformRecursively);
  }

  const { fieldsToTransform, transformedData } = transformToArray(data);
  const combinedData = _.mapValues(transformedData, (item, key) => {
    // @ts-ignore
    return data[key] ? [...data[key], ...item] : item;
  });

  return _.chain(data).omit(fieldsToTransform).assign(combinedData).mapValues(transformRecursively).value();
}

export function transformToArray(data) {
  let fieldsToTransform;
  const transformedData = _.chain(data)
    .keys()
    .filter((key) => {
      return key.split(formArrayTypeDivider).length === 3;
    })
    .tap((toTransform) => {
      fieldsToTransform = toTransform;
    })
    .map((fullFieldName) => {
      const [arrayName, type, fieldName] = fullFieldName.split(formArrayTypeDivider);
      return { arrayName, type, fieldName, fullFieldName };
    })
    .groupBy((item) => `${item.arrayName}.${item.type}`)
    .reduce((acc, item, key) => {
      const [arrayName, type] = key.split('.');
      const arrayRow = item.reduce(
        (acc, rowItem) => ({ ...acc, [rowItem.fieldName]: data[rowItem.fullFieldName] }),
        {}
      );
      const transformedArrayRow = acc[arrayName]
        ? acc[arrayName].concat({ ...arrayRow, type })
        : [{ ...arrayRow, type }];
      return !['products', 'futureProducts', 'futureProductsCCM'].includes(arrayName) ||
        (['products', 'futureProducts', 'futureProductsCCM'].includes(arrayName) && Object.keys(arrayRow).length > 1)
        ? { ...acc, [arrayName]: transformedArrayRow }
        : acc;
    }, {})
    .value();
  return { fieldsToTransform, transformedData };
}

const getProductEndDate = (endDate, prodEnDate, isEnabled, isChronicCareEnabled) => {
  if (!isChronicCareEnabled) {
    return prodEnDate ? (moment(prodEnDate).isAfter(moment()) ? todayDate : endDate) : todayDate;
  }

  if (isEnabled) {
    return endDate || null ? endDate : null;
  }
  if (endDate) {
    return moment(endDate).isAfter(moment()) ? todayDate : endDate;
  }
  return todayDate;
};

export function combineFieldArrays(data) {
  let fieldToCombine;

  const withNormalizedChronicCare = {
    ...data,
    groups:
      data.groups?.map((group) => {
        const groupKeys = Object.keys(group);
        const chronicCareSettings = groupKeys.reduce(
          (acc, itemKey) => {
            if (chronicCareGeneralFormSettings.includes(itemKey)) {
              const lastWord: string = itemKey.split('--').pop()!;
              return { ...acc, [lastWord]: group[itemKey] };
            }
            return acc;
          },
          {
            isEnabled: false,
            start_dt: null,
            end_dt: null
          }
        );
        return Object.keys(group)
          .filter((key) => !chronicCareGeneralFormSettings.includes(key))
          .reduce((acc, key) => {
            const addSettingsToCode = chronicCareCode.find((code) => code === key.split('--')[1]);
            if (key.includes('futureProductsCCM')) return { ...acc, [key]: group[key] };
            const isProductEnabled = group[`products--${addSettingsToCode}--isEnabled`];
            const isChronicCareNotTouched =
              !isProductEnabled &&
              addSettingsToCode &&
              !group[`products--${addSettingsToCode}--end_dt`] &&
              !group[`products--${addSettingsToCode}--start_dt`];
            if (isChronicCareNotTouched) return acc;
            if (addSettingsToCode && key.includes('isEnabled')) {
              const { start_dt, end_dt, isEnabled } = chronicCareSettings;
              const productEndDt = group[`products--${addSettingsToCode}--end_dt`];
              const productStartDt = group[`products--${addSettingsToCode}--start_dt`];
              const endDate = getProductEndDate(end_dt, productEndDt, isProductEnabled, isEnabled);
              return {
                ...acc,
                [`products--${addSettingsToCode}--isEnabled`]: isProductEnabled,
                [`products--${addSettingsToCode}--start_dt`]:
                  isEnabled && isProductEnabled ? moment(start_dt).format(DATE_FORMAT) : productStartDt,
                [`products--${addSettingsToCode}--end_dt`]: endDate ? moment(endDate).format(DATE_FORMAT) : endDate
              };
            }
            return { ...acc, [key]: group[key] };
          }, {});
      }) || []
  };

  const combinedData = _.chain(withNormalizedChronicCare)
    .keys()
    .filter((key) => key.includes(formArrayTypeDivider) && key.split(formArrayTypeDivider).length === 2)
    .tap((toCombine) => {
      fieldToCombine = toCombine;
    })
    .reduce((acc, field) => {
      const [arrayName, type] = field.split(formArrayTypeDivider);
      if (
        Array.isArray(withNormalizedChronicCare[field]) &&
        !withNormalizedChronicCare[field].filter((x) => !!x).length
      )
        return acc;
      if (acc[arrayName]) {
        return {
          ...acc,
          [arrayName]: [...acc[arrayName], ...withNormalizedChronicCare[field].map((item) => ({ type, ...item }))]
        };
      }
      return { ...acc, [arrayName]: [...withNormalizedChronicCare[field].map((item) => ({ type, ...item }))] };
    }, {})
    .omit(fieldToCombine)
    .value();

  return _.chain(withNormalizedChronicCare).omit(fieldToCombine).assign(combinedData).value();
}

const expandDoubleUnderscoreProduct = (product, includeMain = false) => {
  const fields = Object.keys(product).filter((k) => k.includes('__'));
  const base = _.omit(product, fields);
  const variations = fields.map((key) => ({
    ...base,
    member_copay: product[key],
    type: key.split('__')[1]
  }));

  return includeMain ? [{ ...base, member_copay: 0, type: '' }, ...variations] : variations;
};

const expandEmsProduct = (product) => {
  const emsTypes = [...emsMainCodeList];
  if (product.isEfb && !emsTypes.includes('EFB')) {
    emsTypes.push('EFB');
  }

  return emsTypes.map((c) => {
    const item = { ..._.omit(product, ['isEfb']), code: c };
    if (c !== 'EFB' && emsTypes.includes('EFB')) {
      item.type = 'EFB';
    }
    return item;
  });
};

const redefineCareProduct = (product) => {
  if (!product.code.endsWith('CARE')) return [product]; // should never happen

  const baseCode = product.code.replace(/CARE$/, '');
  return { ...product, code: baseCode, type: 'CARE' };
};

const redefinePromoProduct = (product) => {
  if (!product.code.endsWith('PROMO')) return [product]; // should never happen

  const baseCode = product.code.replace(/PROMO$/, '');
  return { ...product, code: baseCode, promotion: 'PROMO_FREENUTRITIONVISITFORCARDIO' };
};

const redefineBundleProduct = (product, bundle) => {
  return { ...product, bundleType: product.bundleType ? bundle : 'STANDALONE' };
};

const cleanProduct = (product) => {
  if (product.bundleType === false) {
    const { bundleType, ...rest } = product;
    return rest;
  }
  return product;
};

const transformProducts = (products) => {
  return products.flatMap((product) => {
    const { code, start_dt } = product;

    if (!start_dt || !moment(start_dt).isValid()) return [];

    const baseDefaults = {
      start_dt: moment(start_dt).format(DATE_FORMAT),
      end_dt: getProductEndDate(product.end_dt, product.end_dt, product.isEnabled, true),
      member_copay: 0,
      cost_per_person: 0,
      app_opt_out: false,
      could_be_less: false,
      code: null,
      type: undefined
    };

    const mergeWithDefaults = (p) => {
      const merged = { ...baseDefaults, ...p };
      if (merged.bundleType === false) {
        delete merged.bundleType;
      }
      return merged;
    };

    if (code === productCodes.bh) return expandDoubleUnderscoreProduct(product).map(mergeWithDefaults);

    if (code === productCodes.p360) return expandDoubleUnderscoreProduct(product, true).map(mergeWithDefaults);

    if (code === productCodes.ems) return expandEmsProduct(product).map(mergeWithDefaults);

    if (careProducts.includes(code)) product = redefineCareProduct(product);

    if (promoProducts.includes(code)) product = redefinePromoProduct(product);

    if (plusProducts.includes(code)) product = redefineBundleProduct(product, 'PLUS');

    if (completeProducts.includes(code)) product = redefineBundleProduct(product, 'COMPLETE');

    return [cleanProduct(mergeWithDefaults(product))];
  });
};

export const transformGroups = (data) => {
  data.start_dt = data.start_dt ? moment(data.start_dt).format(DATE_FORMAT) : null;
  data.end_dt = data.end_dt ? moment(data.end_dt).format(DATE_FORMAT) : null;

  _.remove(data?.groups, _.isUndefined);
  _.forEach(data?.groups, (group) => {
    group.start_dt = group.start_dt ? moment(group.start_dt).format(DATE_FORMAT) : null;
    group.end_dt = group.end_dt ? moment(group.end_dt).format(DATE_FORMAT) : null;
    // @ts-ignore
    _.remove(group.products, (p) => !p.end_dt && !p.start_dt);
    _.forEach(group.products, (item) => {
      if (!item) {
        return;
      }

      item.code = item.type;
      // could_be_less is already in the item after transformRecursively converts flattened keys to array
      const rawCouldBeLess = item.could_be_less;
      item.could_be_less = rawCouldBeLess === 'true' || rawCouldBeLess === true || false;
      // app_opt_out only applies to GM products
      if (item.code === 'GM') {
        item.app_opt_out =
          group['products--GM--app_opt_out'] !== undefined
            ? group['products--GM--app_opt_out']
            : group['products--app_opt_out']
            ? group['products--app_opt_out']
            : false;
      }
      // Note: app_opt_out field is omitted for non-GM products
      item.start_dt = item.start_dt ? moment(item.start_dt).format(DATE_FORMAT) : null;
      item.end_dt = item.end_dt ? moment(item.end_dt).format(DATE_FORMAT) : null;
      delete item.type;
    });
    _.forEach(group.futureProducts, (item) => {
      if (!item) {
        return;
      }

      item.code = item.type;
      // could_be_less is already in the item after transformRecursively converts flattened keys to array
      const rawCouldBeLess = item.could_be_less;
      item.could_be_less = rawCouldBeLess === 'true' || rawCouldBeLess === true || false;
      // app_opt_out only applies to GM products
      if (item.code === 'GM') {
        item.app_opt_out =
          group['futureProducts--GM--app_opt_out'] !== undefined
            ? group['futureProducts--GM--app_opt_out']
            : group['futureProducts--app_opt_out']
            ? group['futureProducts--app_opt_out']
            : false;
      }
      // Note: app_opt_out field is omitted for non-GM products
      delete item.type;
    });
    _.forEach(group.futureProductsCCM, (item) => {
      if (!item) {
        return;
      }
      item.code = item.type;
      item.end_dt = item.end_dt ? item.end_dt : item.isEnabled && item.start_dt ? null : todayDate;
      delete item.type;
    });
    group.products = group?.products && transformProducts(group.products);
    group.futureProducts = group?.futureProducts && transformProducts(group.futureProducts);
    group.planCodes = filterEmptyCodes(group.planCodes);
  });

  // If we have any contacts that are just the type, then remove them
  if (data.contacts) {
    data.contacts = data.contacts.filter((c) => Object.keys(c).length > 1 || Object.keys(c)[0] !== 'type');
  }
};

export const omitExtraFields = (data, allowed) => {
  const simpleFields = _.chain(allowed)
    .omitBy((item) => _.isObjectLike(item))
    .keys()
    .value();
  const complexFields = _.omit(allowed, simpleFields);
  const keyReducer = _.keys(complexFields).reduce((acc, key) => {
    if (_.isArray(allowed[key])) {
      const keyContent = data[key]?.map((ddd) => {
        return omitExtraFields(ddd, _.first(allowed[key]));
      });

      return { ...acc, [key]: keyContent };
    }

    return { ...acc, [key]: omitExtraFields(data[key], allowed[key]) };
  }, {});
  return {
    ..._.pick(data, simpleFields),
    ...keyReducer
  };
};

/**
 * There is a hack that creates an empty field-array entry (with empty codes) once the form is rendered to have a correct field array behavior
 * filter these fields out on publish
 */
const filterEmptyCodes = (codes) => {
  return codes
    ? codes.filter(
        ({ code, classCodes, bpCodes }) => code.code || (classCodes && classCodes.length) || (bpCodes && bpCodes.length)
      )
    : [];
};
